import * as React from 'react';

import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, FormControlLabel, Paper, Radio, Stack, Tooltip, Typography } from '@mui/material';
import { AlertProps } from '@mui/material/Alert';
import Grid from '@mui/material/Grid2';
import ExpandMore from '@mui/icons-material/ExpandMore';
import TaskAlt from '@mui/icons-material/TaskAlt';
import CancelRounded from '@mui/icons-material/CancelRounded';

import SvgIconConsumer from '../../SVGIcons/SVGIconConsumer';
import SVGIconProvider from '../../SVGIcons/SVGIconProvider';

import { Page } from '../../subs/Page';
import { WorkshopTestCase, TestSupportStatus } from "../../types/WorkshopTestCases";
import { WSConfigProductData, WSConfigCombinationData, WSConfigCombinationIfaceData, WSConfigTestCaseGroup, WSConfigTestCaseData } from "../../types/WSConfigData";
import { httpPost } from '../../Web';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import { WSCInputCompletionChip } from "./WSCInputCompletionChip";

// global theming ruins the radio, disable it here while still inheriting dark-mode palette/applyStyles from the ambient theme
const useNoRadioTheme = () => {
    const outerTheme = useTheme();
    return React.useMemo(() => createTheme(outerTheme, {
        components: {
            MuiRadio: {
                styleOverrides: {
                    root: {
                    },
                },
            }
        }
    }), [outerTheme]);
};

export const WSCTestCases = ({ ifaceDataIn, wsConfigProductData, refreshProductData }:
    {
        ifaceDataIn: WSConfigCombinationIfaceData[], wsConfigProductData: WSConfigProductData,
        refreshProductData: (newlyMarkedTestCases?: number) => void
    }) => {
    const [changesDisabled, setChangesDisabled] = React.useState(false);
    const [isConsumer, setIsConsumer] = React.useState(false);
    const [isProvider, setIsProvider] = React.useState(false);

    React.useEffect(() => {
        let noChanges = false;
        if (wsConfigProductData === undefined) noChanges = true;
        else {
            const data = wsConfigProductData.combinationData.find(cd => cd.testCombinationId === wsConfigProductData.key.testCombinationId);
            if (data === undefined) noChanges = true;
            else {
                if (data.isConsumer && data.consumerReadyToTest) noChanges = true;
                if (data.isProvider && data.providerReadyToTest) noChanges = true;
                setIsConsumer(data.isConsumer);
                setIsProvider(data.isProvider);
            }
        }

        setChangesDisabled(noChanges);
    }, [wsConfigProductData]);

    return <Page heading={`Supported Test Cases for ${wsConfigProductData?.productName} ${wsConfigProductData?.productVersion ?? ""}`}>
        {[wsConfigProductData ?
            <InterfaceTCAccordion ifaceDataIn={ifaceDataIn} changesDisabled={changesDisabled}
                wsConfigCombinationData={wsConfigProductData.combinationData.find(cd => cd.testCombinationId === wsConfigProductData.key.testCombinationId) ?? {} as WSConfigCombinationData}
                isConsumer={isConsumer} isProvider={isProvider} refreshProductData={refreshProductData} /> : <></>]}
    </Page>
}

const InterfaceTCAccordion = ({ ifaceDataIn, wsConfigCombinationData, changesDisabled, isConsumer, isProvider, refreshProductData }:
    {
        ifaceDataIn: WSConfigCombinationIfaceData[], changesDisabled: boolean,
        wsConfigCombinationData: WSConfigCombinationData,
        isConsumer: boolean, isProvider: boolean,
        refreshProductData: (newlyMarkedTestCases?: number) => void
    }) => {

    return <Box>
        {(
            ifaceDataIn.map(wspi => wspi.supportedByProduct ?
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant='bold'>{wspi.interfaceName ?? 'Error - interface not found'}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {
                            <TestCaseAccordion testCaseGroups={wspi.testCaseGroups ?? []} refreshProductData={refreshProductData}
                                wsConfigCombinationData={wsConfigCombinationData}
                                isConsumer={isConsumer} isProvider={isProvider} changesDisabled={changesDisabled} />
                        }
                    </AccordionDetails>
                </Accordion>
                : <></>
            )
        )}
    </Box>
}

const TestCaseAccordion = ({ testCaseGroups, refreshProductData, wsConfigCombinationData, isConsumer, isProvider, changesDisabled }:
    {
        testCaseGroups: WSConfigTestCaseGroup[], changesDisabled: boolean,
        wsConfigCombinationData: WSConfigCombinationData,
        isConsumer: boolean, isProvider: boolean,
        refreshProductData: (newlyMarkedTestCases?: number) => void
    }) => {

    return <>{
        <>
            {(
                testCaseGroups.map(group =>
                    <TestCaseAccordionGroup testCaseGroup={group} refreshProductData={refreshProductData}
                        wsConfigCombinationData={wsConfigCombinationData}
                        isConsumer={isConsumer} isProvider={isProvider} changesDisabled={changesDisabled} />
            ))}
        </>
    }
    </>
}

const TestCaseAccordionGroup = ({ testCaseGroup, refreshProductData, wsConfigCombinationData, isConsumer, isProvider, changesDisabled }:
    {
        testCaseGroup: WSConfigTestCaseGroup, changesDisabled: boolean,
        wsConfigCombinationData: WSConfigCombinationData,
        isConsumer: boolean, isProvider: boolean,
        refreshProductData: (newlyMarkedTestCases?: number) => void
    }) => {
    const [tcGroup, setTcGroup] = React.useState<WSConfigTestCaseGroup>(testCaseGroup);
    const [groupComplete, setgroupComplete] = React.useState<boolean>(false);
    const { showSnackbar } = useSnackbar();

    const showUserFeedback = React.useCallback((msg: string, severity: AlertProps['severity'] = 'error') => {
        showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        setTcGroup(testCaseGroup);
    }, [testCaseGroup]);

    React.useEffect(() => {
        let undefTC = tcGroup.testCases.filter(tc => tc.wstcId === 0);
        let undecided = tcGroup.testCases.filter(tc => tc.supportStatus === TestSupportStatus.Undecided);

        setgroupComplete(undefTC.length === 0 && undecided.length === 0);
    }, [tcGroup]);

    const setTestCaseConfig = async (testCaseIds: number[], supported: TestSupportStatus) => {

        let updatedWSTestCaes: Promise<any>[] = [];
        let abortController = new AbortController();

        if (changesDisabled) {
            showUserFeedback('Changes are disabled while the product is marked as \'ready to test\'.');
            return;
        }

        let workstopTestcase: WorkshopTestCase | undefined = undefined;
        let newlyMarkedTestCases: number = 0;
        let testCaseGroupLoc = tcGroup;

        testCaseGroupLoc.testCases.forEach(testCase => {
            if (testCaseIds.includes(testCase.testCaseId)) {
                workstopTestcase = {
                    workshopProductId: testCase.workshopProductId,
                    testCombinationId: testCase.testCombinationId,
                    testCaseId: testCase.testCaseId,
                    supportStatus: supported,
                    id: testCase.wstcId
                } as WorkshopTestCase;
                updatedWSTestCaes.push(httpPost(`/api/workshopproduct/workshoptestcase/update`, workstopTestcase, abortController));
                if (testCase.wstcId === 0) newlyMarkedTestCases++;
            }
        });

        const responses = await Promise.all(updatedWSTestCaes);
        const someFailed = responses.find(response => response.failed);
        if ((someFailed)) {
            showUserFeedback(someFailed.errorText);
            return;
        }

        responses.forEach(response => {
            const updatedTC = response.result as WorkshopTestCase;
            let tcIndex = testCaseGroupLoc.testCases.findIndex(tc => tc.testCaseId === updatedTC.testCaseId);
            if (tcIndex !== -1) {
                testCaseGroupLoc.testCases[tcIndex].supportStatus = updatedTC.supportStatus;
                testCaseGroupLoc.testCases[tcIndex].wstcId = updatedTC.id ?? 0;
            }
        });

        setTcGroup({ ...testCaseGroupLoc });
        refreshProductData(newlyMarkedTestCases);
        showUserFeedback('Testcase support saved.', 'success');
    }

    return <>
        {
            <AccordionDetails>
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Stack width={'100%'} direction={'row'} justifyContent={'space-between'}>
                            {testCaseGroup.group}
                            <WSCInputCompletionChip completed={groupComplete} />
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        {<TestCasesRadioGroup testCases={testCaseGroup.testCases} changesDisabled={changesDisabled}
                            wsConfigCombinationData={wsConfigCombinationData}
                            isConsumer={isConsumer} isProvider={isProvider} setTestCaseConfig={setTestCaseConfig} />}
                    </AccordionDetails>
                </Accordion>
            </AccordionDetails>
        }
    </>
}

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: '#fff',
    ...theme.typography.body2,
    padding: 10,
    textAlign: 'left',
    minHeight: 20,
    maxHeight: 60,
    scrollBehavior: 'auto',
    color: theme.palette.text.primary,
    ...theme.applyStyles('dark', {
        backgroundColor: '#1A2027',
    }),
}));

const Item2 = styled(Paper)(({ theme }) => ({
    backgroundColor: '#fff',
    ...theme.typography.body2,
    padding: 10,
    textAlign: 'right',
    fontWeight: 'bold',
    minHeight: 30,
    maxHeight: 30,
    scrollBehavior: 'auto',
    color: theme.palette.text.primary,
    ...theme.applyStyles('dark', {
        backgroundColor: '#1A2027',
    }),
}));

const TestCasesRadioGroup = ({ testCases, wsConfigCombinationData, isConsumer, isProvider, changesDisabled, setTestCaseConfig }:
    {
        testCases: WSConfigTestCaseData[], isConsumer: boolean, isProvider: boolean, changesDisabled: boolean,
        wsConfigCombinationData: WSConfigCombinationData,
        setTestCaseConfig: (utcId: number[], support: TestSupportStatus) => void
    }) => {
    const noRadioTheme = useNoRadioTheme();

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (changesDisabled) {
            setTestCaseConfig([], TestSupportStatus.Undecided);     // parent shows the 'changes disabled' message
            return;
        }
        const supported = (event.target as HTMLInputElement).value;
        let supportStatus: TestSupportStatus = TestSupportStatus.Undecided;
        switch (supported) {
            case 'client_supported':
                supportStatus = TestSupportStatus.ClientSupported;
                break;
            case 'server_supported':
                supportStatus = TestSupportStatus.ServerSupported;
                break;
            case 'supported':
                supportStatus = TestSupportStatus.Supported;
                break;
            case 'unsupported':
                supportStatus = TestSupportStatus.Unsupported;
                break;
        }
        if (!isConsumer && (supportStatus === TestSupportStatus.ClientSupported)) return;
        if (!isProvider && (supportStatus === TestSupportStatus.ServerSupported)) return;
        testCases.forEach(testCase => testCase.supportStatus = supportStatus);      // set locally so that UI updates immediately, will be overridden by db values if some fail
        setTestCaseConfig(testCases.map(testCase => testCase.testCaseId), supportStatus);
    };

    return <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Grid container spacing={{ xs: 2, md: 5 }}>
            <ThemeProvider theme={noRadioTheme}>
                <Grid size={(isConsumer && isProvider ? 8 : 10)}>
                    <Item2 elevation={0} >Test Cases - mark all</Item2>
                </Grid>
                {isConsumer && isProvider && <Grid size={1}>
                    <Tooltip title={`Mark all as only supported on ${wsConfigCombinationData.combinationConsumer} side`}
                        enterDelay={1500} leaveDelay={0}
                        placement="top-start" sx={{
                            '& .MuiTooltip-tooltip': {
                                pointerEvents: 'none', // mouse events pass through the tooltip
                            },
                        }}>
                        <FormControlLabel value="client_supported"
                            control={<Radio
                                checked={false}
                                disabled={!isConsumer}
                                color="success"
                                onChange={handleChange}
                                icon=<SvgIconConsumer />
                            />}
                            label="" labelPlacement='start' />
                    </Tooltip>
                </Grid>}
                {isConsumer && isProvider && <Grid size={1}>
                    <Tooltip title={`Mark all as only supported on ${wsConfigCombinationData.combinationProvider} side`}
                        enterDelay={1500} leaveDelay={0}
                        placement="top-start" sx={{
                            '& .MuiTooltip-tooltip': {
                                pointerEvents: 'none', // mouse events pass through the tooltip
                            },
                        }}>
                        <FormControlLabel value="server_supported"
                            control={<Radio
                                checked={false}
                                disabled={!isProvider}
                                color="success"
                                onChange={handleChange}
                                icon=<SVGIconProvider />
                            />}
                            label="" labelPlacement='start' />
                    </Tooltip>
                </Grid>}
                <Grid size={1}>
                    <Tooltip title={"Mark all as " + (isConsumer && isProvider ? "fully supported" : "supported")}
                        enterDelay={1500} leaveDelay={0}
                        placement="top-start" sx={{
                            '& .MuiTooltip-tooltip': {
                                pointerEvents: 'none', // mouse events pass through the tooltip
                            },
                        }}>
                        <FormControlLabel value="supported"
                            control={<Radio
                                checked={false}
                                color="success"
                                onChange={handleChange}
                                icon=<TaskAlt />
                            />}
                            label="" labelPlacement='start' />
                    </Tooltip>
                </Grid>
                <Grid size={1}>
                    <Tooltip title="Mark all as not supported"
                        enterDelay={1500} leaveDelay={0}
                        placement="top-start" sx={{
                            '& .MuiTooltip-tooltip': {
                                pointerEvents: 'none', // mouse events pass through the tooltip
                            },
                        }}>
                        <FormControlLabel value="unsupported"
                            control={<Radio
                                checked={false}
                                color="error"
                                onChange={handleChange}
                                icon=<CancelRounded />
                            />}
                            label="" />
                    </Tooltip>
                </Grid>
            </ThemeProvider>
            {testCases.map(testCase =>
                <TestCaseRadioGroup testCase={testCase} isConsumer={isConsumer} isProvider={isProvider}
                    wsConfigCombinationData={wsConfigCombinationData}
                    changesDisabled={changesDisabled} setTestCaseConfig={setTestCaseConfig} />
            )}
        </Grid>
    </div>
}

const TestCaseRadioGroup = ({ testCase, wsConfigCombinationData, isConsumer, isProvider, changesDisabled, setTestCaseConfig }:
    {
        testCase: WSConfigTestCaseData, isConsumer: boolean, isProvider: boolean, changesDisabled: boolean,
        wsConfigCombinationData: WSConfigCombinationData,
        setTestCaseConfig: (utcId: number[], support: TestSupportStatus) => void
    }) => {
    const [expanded, setExpanded] = React.useState<boolean>(false);
    const noRadioTheme = useNoRadioTheme();

    const handleAccordionChange =
        () => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded);
        };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (changesDisabled) {
            setTestCaseConfig([], TestSupportStatus.Undecided);     // parent shows the 'changes disabled' message
            return;
        }
        const supported = (event.target as HTMLInputElement).value;
        let supportStatus: TestSupportStatus = TestSupportStatus.Undecided;
        switch (supported) {
            case 'client_supported':
                supportStatus = TestSupportStatus.ClientSupported;
                break;
            case 'server_supported':
                supportStatus = TestSupportStatus.ServerSupported;
                break;
            case 'supported':
                supportStatus = TestSupportStatus.Supported;
                break;
            case 'unsupported':
                supportStatus = TestSupportStatus.Unsupported;
                break;
        }
        if (!isConsumer && supportStatus === TestSupportStatus.ClientSupported) return;
        if (!isProvider && supportStatus === TestSupportStatus.ServerSupported) return;
        testCase.supportStatus = supportStatus;     // set locally so that UI updates immediately, will be overridden by db values if some fail
        setTestCaseConfig([testCase.testCaseId], supportStatus);
    };

    return <>
        <Grid size={(isConsumer && isProvider ? 8 : 10)}>
            {testCase.description ?
                <AccordionDetails>
                    <Accordion defaultExpanded={false} onChange={handleAccordionChange()}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Stack width={'100%'} spacing={10} direction={'row'} justifyContent={'space-between'}>
                                <Box width={'90%'}> <Item elevation={0}>{testCase.name}</Item> </Box>
                                <Divider orientation="vertical" flexItem />
                                <Typography variant='subscript'>{expanded ? 'Hide description' : 'Show description'}</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Divider />
                            <Item elevation={0}>{testCase.description}</Item>
                            <Divider />
                        </AccordionDetails>
                    </Accordion>
                </AccordionDetails>
                :
                <Item elevation={3}>{testCase.name}</Item>}
        </Grid>
        <ThemeProvider theme={noRadioTheme}>
            {isConsumer && isProvider && <Grid size={1}>
                <Tooltip title={`Only supported on ${wsConfigCombinationData.combinationConsumer} side`}
                    enterDelay={1500} leaveDelay={0}
                    placement="top-start" sx={{
                        '& .MuiTooltip-tooltip': {
                            pointerEvents: 'none', // mouse events pass through the tooltip
                        },
                    }}>
                    <FormControlLabel value="client_supported"
                        control={<Radio
                            checked={testCase.supportStatus === TestSupportStatus.ClientSupported}
                            disabled={!isConsumer}
                            color="success"
                            onChange={handleChange}
                        />}
                        label="" labelPlacement='start' />
                </Tooltip>
            </Grid>}
            {isConsumer && isProvider && <Grid size={1}>
                <Tooltip title={`Only supported on ${wsConfigCombinationData.combinationProvider} side`}
                    enterDelay={1500} leaveDelay={0}
                    placement="top-start" sx={{
                        '& .MuiTooltip-tooltip': {
                            pointerEvents: 'none', // mouse events pass through the tooltip
                        },
                    }}>
                    <FormControlLabel value="server_supported"
                        control={<Radio
                            checked={testCase.supportStatus === TestSupportStatus.ServerSupported}
                            disabled={!isProvider}
                            color="success"
                            onChange={handleChange}
                        />}
                        label="" labelPlacement='start' />
                </Tooltip>
            </Grid>
            }
            <Grid size={1}>
                <Tooltip title={isConsumer && isProvider ? "Fully supported" : "Supported"}
                    enterDelay={1500} leaveDelay={0}
                    placement="top-start" sx={{
                        '& .MuiTooltip-tooltip': {
                            pointerEvents: 'none', // mouse events pass through the tooltip
                        },
                    }}                >
                    <FormControlLabel value="supported"
                        control={<Radio
                            checked={((testCase.supportStatus === TestSupportStatus.Supported)
                                || (!isConsumer && testCase.supportStatus === TestSupportStatus.ServerSupported)
                                || (!isProvider && testCase.supportStatus === TestSupportStatus.ClientSupported))}
                            color="success"
                            onChange={handleChange}
                        />}
                        label="" labelPlacement='start' />
                </Tooltip>
            </Grid>
            <Grid size={1}>
                <Tooltip title={"Not supported"}
                    enterDelay={1500} leaveDelay={0}
                    placement="top-start" sx={{
                        '& .MuiTooltip-tooltip': {
                            pointerEvents: 'none', // mouse events pass through the tooltip
                        },
                    }}                >
                    <FormControlLabel value="unsupported"
                        control={<Radio
                            checked={((testCase.supportStatus === TestSupportStatus.Unsupported)
                                || (isConsumer && testCase.supportStatus === TestSupportStatus.ServerSupported)
                                || (isProvider && testCase.supportStatus === TestSupportStatus.ClientSupported))}
                            color="error"
                            onChange={handleChange}
                        />}
                        label="" />
                </Tooltip>
            </Grid>
        </ThemeProvider>
    </>
}
